import ProductResultItemTemplate from './items/product-result-item.template';
import { FunctionalComponent, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import InspirationResultItemTemplate from './items/inspiration-result-item.template';
import ServiceResultItemTemplate from './items/service-result-item.template';
import CategoryResultItemTemplate from './items/category-result-item.template';
import PageResultItemTemplate from './items/page-result-item.template';
import {
  BaseIndexItem,
  CategoryIndexItem,
  InspirationIndexItem,
  ProductIndexItem,
  SchemaName,
  ServiceIndexItem,
} from '@cewe-onsitesearch/schemas';
import { OnsiteSearchComponent } from '../../cwc-onsite-search';
import { SearchResponse } from '../../model/search-response.model';
import { ResultGridPosition } from '../cwc-onsite-search.template';

/**
 * Definition of  parameters that the template needs to work.
 */
interface ResultsTemplateProps {
  /**
   * The class instance that provides controller functionality for the template
   */
  controller: OnsiteSearchComponent;
  type: SchemaName;
  searchResponse: SearchResponse;
  gridPosition: ResultGridPosition;
  showMore: boolean;
  initialVisibleRows: number;
  columns: number;
  // Specify parameters that are required to be passed down to the template here.
}

const ResultsTemplate: FunctionalComponent<ResultsTemplateProps> = ({
  controller,
  type,
  searchResponse,
  gridPosition,
  showMore,
  initialVisibleRows,
  columns,
}) => {
  if (!controller.searchResponse?.results[type] || controller.searchResponse.results[type].hits.length === 0) {
    return '';
  }

  const items = controller.searchResponse.results[type]?.hits ?? [];
  const totalInitialVisibleItems = initialVisibleRows * columns;
  const hasShowMoreFeature: boolean = showMore === true && items.length > totalInitialVisibleItems;
  const hasMoreItems: boolean = items.length > totalInitialVisibleItems;
  const moreResultsToggleId: string = 'results-list-toggle_' + type + Math.random();
  const queryID: string = searchResponse.results[type].queryID || new Date().getTime().toString();

  return (
    <results length={items.length} type={type} position={gridPosition}>
      <results-head>
        <h2>{controller.textResources.results[type].headline}</h2>
      </results-head>
      <results-list>
        <input type="checkbox" class="more-results-toggle" id={moreResultsToggleId} key={moreResultsToggleId} />
        {items.map((item: BaseIndexItem, position: number) => {
          const elements = [];
          const isItemHidden: boolean = hasShowMoreFeature && position >= totalInitialVisibleItems;
          switch (type) {
            case SchemaName.product:
              elements.push(
                <ProductResultItemTemplate
                  listType={type}
                  queryID={queryID}
                  hidden={isItemHidden}
                  product={item as ProductIndexItem}
                  position={position + 1}
                  ref={controller}
                  key={queryID + '-' + position}
                  textResources={controller.textResources.results.product}
                />,
              );
              break;
            case SchemaName.inspiration:
              elements.push(
                <InspirationResultItemTemplate
                  listType={type}
                  queryID={queryID}
                  hidden={isItemHidden}
                  item={item as InspirationIndexItem}
                  position={position + 1}
                  ref={controller}
                  key={queryID + '-' + position}
                  textResources={controller.textResources.results.inspiration}
                />,
              );
              break;
            case SchemaName.service:
              elements.push(
                <ServiceResultItemTemplate
                  listType={type}
                  queryID={queryID}
                  hidden={isItemHidden}
                  item={item as ServiceIndexItem}
                  position={position + 1}
                  ref={controller}
                  key={queryID + '-' + position}
                  textResources={controller.textResources.results.service}
                />,
              );
              break;
            case SchemaName.category:
              elements.push(
                <CategoryResultItemTemplate
                  listType={type}
                  queryID={queryID}
                  hidden={isItemHidden}
                  item={item as CategoryIndexItem}
                  position={position + 1}
                  ref={controller}
                  key={queryID + '-' + position}
                  textResources={controller.textResources.results.category}
                />,
              );
              break;
            case SchemaName.page:
              elements.push(
                <PageResultItemTemplate
                  listType={type}
                  queryID={queryID}
                  hidden={isItemHidden}
                  item={item}
                  position={position + 1}
                  ref={controller}
                  key={queryID + '-' + position}
                  textResources={controller.textResources.results.page}
                />,
              );
              break;
          }

          return elements;
        })}
        {hasShowMoreFeature && hasMoreItems ? (
          <label
            key={'btn'}
            htmlFor={moreResultsToggleId}
            class="more-results-btn"
            onClick={() => controller.onShowMoreResults(type)}
            data-text={controller.textResources.results.showMoreResults.replace('{{count}}', String(items.length))}
            data-text-checked={controller.textResources.results.showMoreResultsClose}
          ></label>
        ) : (
          ''
        )}
      </results-list>
    </results>
  );
};

export default ResultsTemplate;
